<style scoped>
.column_heading {
	font-size: 16px;
	font-weight: bold;
}

.selected_row {
	background-color: #DAFFDA
}
</style>

<template>
	<div>
		<div style="height: 35px" v-if="records.length > 0">
			<div class="float-right" v-if="can_change_columns">
				<v-btn icon @click="show_column_select = true" class="d-print-none">
					<v-icon>mdi-cog-outline</v-icon>
				</v-btn>
			</div>
			<div class="float-right" v-if="can_print">
				<v-btn icon @click="do_print()" class="d-print-none">
					<v-icon>mdi-printer</v-icon>
				</v-btn>				
			</div>
			<div class="text-center">
				Found {{ records.length }} records that match criteria
			</div>
		</div>

		<v-simple-table
			fixed-header
			:height="height"
		>
			<template v-slot:default>
				<thead>
					<v-draggable v-model="columns" tag="tr">
						<th
							v-for="column in columns"
							:key="column.name"
							scope="col"
							class="column_heading text-no-wrap"
							@click.exact="toggle_sort_column(column.name)"
							@click.shift.exact="toggle_sort_column(column.name, false)"
							:style="`background-color: ` + column.header_color"
						>
							{{ column.label }}
							{{ get_arrow(column.name) }}
							<sup v-if="sortcols.length>1 && sortcols.indexOf(column.name) != -1">{{ sortcols.indexOf(column.name) + 1 }}</sup>
						</th>
					</v-draggable>
				</thead>
				<tbody>
					<tr
						v-for="record in filtered_records"
						:key="record.id"
						:class="{ selected_row: record.id == selected_id }"				
						@click="$emit('row_clicked', record)"
					>
						<td
							v-for="column in columns"
							:key ="column.name"
							class="text-no-wrap"
						>
							<span v-if="column.type == 'span'">{{ get_column(record, column.type, column.name) }}</span>
							<span v-if="column.type == 'date'">{{ format_date(get_column(record, column.type, column.name)) }}</span>
							<a v-if="column.type == 'href'" @click.stop="$router.push(get_column(record, column.type, column.uri))">{{ get_column(record, column.type, column.name) }}</a>
							<jb-perc v-if="column.type == 'perc'" :overcolor="column.overcolor" :value="get_column(record, column.type, column.name)"/>
							<div v-if="column.type == 'value'" width="100%" class="text-right">
								{{ record.currency.symbol }} {{ get_column(record, column.type, column.name).toFixed(2) }}
							</div>
						</td>
					</tr>
				</tbody>
			</template>
		</v-simple-table>

		<v-dialog v-model="show_column_select" max-width="300" scrollable>
			<v-card>
				<v-card-title>
					Show columns
				</v-card-title>
				<v-card-text>
					<div
						v-for="col in available_columns"
						:key="col.name"
						@click="toggle_column(col.name)"
					>
						<v-checkbox
							:label="col.label"
							:input-value="column_is_shown(col.name)"
							dense hide-details
						/>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import JBPerc from "@/components/controls/jbperc";

import { dateMixin } from "@/mixins/misc/dateMixin.js"

export default {
	mixins: [ dateMixin ],
	props: {
		selected_id: {
			type: Number,
			default: 0
		},
		can_change_columns: {
			type: Boolean,
			default: true
		},
		can_sort_columns: {
			type: Boolean,
			default: false
		},
		can_print: {
			type: Boolean,
			default: true
		},
		height: {
			type: Number,
			default: undefined
		},
		show_columns: {
			type: Array,
			default: function() { return [] }
		},
		available_columns: {
			type: Array,
			default: function() { return [] }			
		},
		sort_columns: {
			type: Array,
			default: function() { return [] }			
		},
		records: {
			type: Array,
			default: function() { return [] }
		},
	},
	data() {
		return {
			show_column_select: false,
			columns: [],
			sortcols: Array(),
		}
	},
	created() {
		// copy it
		this.sortcols = Array();
		this.sort_columns.forEach(row => this.sortcols.push(row))
	},
	computed: {
		filtered_records() {
			// need to make a copy of our array or we end up sorting the original...
			let result = [];
			this.records.forEach(row => result.push(row));

			if (this.sortcols.length > 0) {
				/* eslint-disable no-unused-vars */
				/* row_a and row_b are used inside of eval which lint can't see... */
				result.sort((row_a, row_b) => {
					for(let i = 0; i < this.sortcols.length; i++) {
						let column_name = this.sortcols[i];
						let sort_order = 1;

						if (column_name.substr(0,1) == '-') {
							column_name = column_name.substr(1);
							sort_order = -1;
						}

						let parts = column_name.split(".");
						let val_a = '';
						let val_b = '';

						for(let p = 0; p < parts.length; p++) {
							if (p == 0) {
								val_a = eval('row_a.' + parts[p]);
								val_b = eval('row_b.' + parts[p]);
							} else {
								val_a = eval('val_a.' + parts[p]);
								val_b = eval('val_b.' + parts[p]);
							}
						}

						if (val_a < val_b) {
							return -sort_order;
						} else if (val_a > val_b) {
							return sort_order;
						}
					}

					return 0; // equal?
				})
				/* eslint-enable no-unused-vars */
			}

			return result;
		},
	},
	components: {
		'jb-perc': JBPerc
	},
	mounted() {
		this.update_columns()
	},
	watch: {
		'show_columns': function() {
			this.update_columns();
		},
		'sort_columns': function() {
			this.sortcols = Array();
			this.sort_columns.forEach(row => this.sortcols.push(row))
		},
		'columns': function(newValue, oldValue) {
			// Note that as we emit our columns_changed, 
			// we could be updating our show_columns property and that would in turn trigger our watch.
			// This is why we check if we've actually changed our columns before calling emit.

			// console.log('watch columns');

			let old_columns = this.make_column_names(oldValue);
			let new_columns = this.make_column_names(newValue);
			let changed = false;
			if (old_columns.length != new_columns.length) {
				changed = true;
			} else {
				for(let i = 0; i < old_columns.length && !changed; i++) {
					if (old_columns[i] != new_columns[i]) {
						changed = true;
					}
				}
			}

			if (changed) {
				// console.log('emit columns_changed');
				// console.log(old_columns);
				// console.log(new_columns);
				this.$emit('columns_changed', new_columns);
			}
		}
	},
	methods: {
		do_print() {
			window.print();
		},
		get_arrow(column) {
			if (this.sortcols.includes(column)) {
				return '▼';
			} else if (this.sortcols.includes("-" + column)) {
				return '▲';
			}
			return '';
		},
		toggle_sort_column(column, clear = true) {
			if (this.can_sort_columns) {
				let idx = this.sortcols.indexOf(column);
				let descending = false;
				if (idx == -1) {
					idx = this.sortcols.indexOf("-" + column);
					descending = true;
				}
				if (idx == -1) {
					if (clear) {
						this.sortcols = [];
					}

					// add it
					this.sortcols.push(column);
				} else {
					// toggle it
					if (descending) {
						this.sortcols[idx] = column;
					} else {
						this.sortcols[idx] = "-" + column;
					}
				}

				this.$emit('sort_changed', this.sortcols);
			};
		},
		update_columns() {
			let defs = [];

			this.available_columns.forEach(col => {
				defs[col.name] = col;
			});

			this.columns = [];
			// console.log(this.show_columns);

			this.show_columns.forEach(col => {
				if (col in defs) {
					let avail_column = defs[col];
					let display_column = {};

					// these should always be there
					display_column.label = avail_column.label;
					display_column.name = avail_column.name;
					display_column.type = avail_column.type;

					// these are optional
					if (avail_column.hasOwnProperty('header_color')) {
						display_column.header_color = avail_column.header_color;
					} else {
						display_column.header_color = 'white';						
					}

					this.columns.push(display_column);
				}
			});
		},
		make_column_names(arr) {
			let column_names = [];

			arr.forEach(col => {
				column_names.push(col.name);
			});

			return column_names;
		},
		column_is_shown(colname) {
			let i = 0;
			for (i == 0; i < this.columns.length; i++) {
				if (this.columns[i].name == colname) {
					return true;
				}
			}
			return false;
		},
		toggle_column(colname) {
			let i = 0;

			// if we can find it, remove it
			for (i = 0; i < this.columns.length; i++) {
				if (this.columns[i].name == colname) {
					this.columns.splice(i, 1);
					// for some reason our watcher isn't triggered so we'll emit here as well...
					this.$emit('columns_changed', this.make_column_names(this.columns));
					return true;
				}
			}

			// if we couldn't find it, add it
			for (i = 0; i < this.available_columns.length; i++) {
				if (this.available_columns[i].name == colname) {
					this.columns.push(this.available_columns[i]);
					// for some reason our watcher isn't triggered so we'll emit here as well...
					this.$emit('columns_changed', this.make_column_names(this.columns));
					return true;
				}
			}

			console.log("Couldn't find " + colname);

			return false;
		},
		get_column(from_row, column_type, column_name) {
			let i = 0;
			let value = '';
			let parts = column_name.split(".");

			for(i = 0; i < parts.length; i++) {
				if (i == 0) {
					value = eval('from_row.' + parts[i]);
				} else {
					value = eval('value.' + parts[i]);
				}

				if (column_type == 'date') {
					if (value) {
						value = new Date(value);
					}
				} else if (!value) {
					if (column_type == 'value') {
						value = 0;
					} else if (column_type == 'perc') {
						value = 0;
					} else {
						value = '';				
					}

					return value;
				}
			}

			return value;
		},		
	}
}
</script>
